import React, { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Button, ClassicTable } from "components";
import { PosSubscriptionServices } from "globalConstants";
import { capitalize } from "helpers";
import { initialSubscriptionValues } from "mappers";
import {
	CadenceTypes,
	MonthlyAndAnnualDdl,
	MonthlyDdl,
	OneTimeDdl,
} from "types/enums";
import { floatWithComma2Normalizer } from "validators";

import Footer from "./footer";
import { getCost } from "./getCost";
import { getTotalPrice } from "./getTotalPrice";
import { setDiscount } from "./setDiscount";
import { useGetRow } from "./useGetRow";

import styles from "./posSubscriptionSection.module.scss";

type PosSubscriptionProps = {
	sectionName: string;
	isLoading?: boolean;
	defaultValues?: any;
	isNailSoftProduct?: boolean;
	isCalendariseProduct?: boolean;
	showMarketingServices?: boolean;
	productType: string;
};

const PosSubscription = ({
	sectionName,
	isLoading,
	defaultValues,
	isNailSoftProduct = false,
	isCalendariseProduct = false,
	showMarketingServices = false,
	productType,
}: PosSubscriptionProps) => {
	const prevProductTypeRef = useRef(
		productType,
	) as React.MutableRefObject<string>;

	const [prevShowMarketingServices, setPrevShowMarketingServices] =
		useState<boolean>(showMarketingServices);

	const {
		register,
		setValue,
		formState: { errors },
		trigger,
		getFieldState,
		clearErrors,
		control,
		getValues,
	} = useFormContext();

	const sectionValues = useWatch({
		control,
		name: `${sectionName}.subscriptions`,
		defaultValue: defaultValues,
	});

	// Set the total price to form values
	const totalPrice = getTotalPrice(sectionValues);

	useEffect(() => {
		register(`${sectionName}.subscriptions.totalPrice`, {
			value: totalPrice,
		});
	}, [register, sectionName, totalPrice]);

	useEffect(() => {
		if (prevProductTypeRef.current !== productType) {
			// Reset the subscription values when the product changes
			setValue("pointOfSale.subscriptions", initialSubscriptionValues);

			const services = [
				PosSubscriptionServices.PosSystem.name,
				PosSubscriptionServices.MarketingServices.name,
				PosSubscriptionServices.IpPhoneService.name,
				PosSubscriptionServices.TapToPay.name,
				PosSubscriptionServices.SmsService.name,
				PosSubscriptionServices.SmsMessage.name,
				PosSubscriptionServices.SmsSetup.name,
				PosSubscriptionServices.SalonInTheBox.name,
			];

			services.forEach(service => {
				setValue(
					`pointOfSale.subscriptions.${service}.billingCadence`,
					initialSubscriptionValues[service].billingCadence,
				);
			});

			prevProductTypeRef.current = productType;
		}
	}, [productType, setValue]);

	const CadenceTypesOptions = CadenceTypes.getOptions();

	const createFieldName = (serviceName: string): string => {
		return `${sectionName}.subscriptions.${serviceName}`;
	};

	const PosSystemName = createFieldName(
		PosSubscriptionServices.PosSystem.name,
	);
	const MarketingServiceName = createFieldName(
		PosSubscriptionServices.MarketingServices.name,
	);
	const IpPhoneServiceName = createFieldName(
		PosSubscriptionServices.IpPhoneService.name,
	);
	const TapToPayName = createFieldName(PosSubscriptionServices.TapToPay.name);
	const SalonInTheBoxName = createFieldName(
		PosSubscriptionServices.SalonInTheBox.name,
	);
	const SmsServiceName = createFieldName(
		PosSubscriptionServices.SmsService.name,
	);
	const SmsMessageName = createFieldName(
		PosSubscriptionServices.SmsMessage.name,
	);
	const SmsSetupName = createFieldName(PosSubscriptionServices.SmsSetup.name);

	const handleCadencePosSystemChange = value => {
		const sectionValues = getValues(`${sectionName}.subscriptions`);
		handleCostChange(PosSubscriptionServices.PosSystem.name);

		if (
			(showMarketingServices && isCalendariseProduct) ||
			isNailSoftProduct
		) {
			setValue(`${MarketingServiceName}.billingCadence`, value as never, {
				shouldDirty: true,
			});
			handleCostChange(PosSubscriptionServices.MarketingServices.name);
			setDiscount(sectionValues, setValue, sectionName);
		}
	};
	const handleCadenceMarketingServiceChange = value => {
		const sectionValues = getValues(`${sectionName}.subscriptions`);

		setValue(`${PosSystemName}.billingCadence`, value as never, {
			shouldDirty: true,
		});
		handleCostChange(PosSubscriptionServices.PosSystem.name);
		handleCostChange(PosSubscriptionServices.MarketingServices.name);

		setDiscount(sectionValues, setValue, sectionName);
	};
	const handleCadenceChange = (value, serviceName) => {
		const quantityFieldName = `${createFieldName(serviceName)}.quantity`;

		if (value?.name === CadenceTypes.Consumption.name) {
			setValue(quantityFieldName, null);
		} else {
			setValue(quantityFieldName, "1");
		}
	};

	const handleQuantityChange = serviceName => {
		handleCostChange(serviceName);
	};

	const handleStatusPosSystemChange = (value: boolean) => {
		const sectionValues = getValues(`${sectionName}.subscriptions`);

		handleStatusChange(PosSubscriptionServices.PosSystem.name, value);

		if (isNailSoftProduct) {
			!value &&
				setValue(`${SalonInTheBoxName}.isSubscribed`, false as never, {
					shouldDirty: true,
				});
		}

		if (
			(showMarketingServices && isCalendariseProduct) ||
			isNailSoftProduct
		) {
			setDiscount(sectionValues, setValue, sectionName);
		}
	};
	const handleStatusMarketingServiceChange = (value: boolean) => {
		const sectionValues = getValues(`${sectionName}.subscriptions`);

		handleStatusChange(
			PosSubscriptionServices.MarketingServices.name,
			value,
		);
		setDiscount(sectionValues, setValue, sectionName);
	};
	const handleStatusIpPhoneServiceChange = (value: boolean) => {
		handleStatusChange(PosSubscriptionServices.IpPhoneService.name, value);
	};
	const handleStatusTapToPayChange = (value: boolean) => {
		handleStatusChange(PosSubscriptionServices.TapToPay.name, value);
	};
	const handleStatusSalonInTheBoxChange = (value: boolean) => {
		value &&
			setValue(`${PosSystemName}.isSubscribed`, true as never, {
				shouldDirty: true,
			});
		handleStatusChange(PosSubscriptionServices.SalonInTheBox.name, value);
	};
	const handleStatusSmsServiceChange = (value: boolean) => {
		handleStatusChange(PosSubscriptionServices.SmsService.name, value);
	};
	const handleStatusSmsMessageServiceChange = (value: boolean) => {
		handleStatusChange(PosSubscriptionServices.SmsMessage.name, value);
	};
	const handleStatusSmsSetupServiceChange = (value: boolean) => {
		handleStatusChange(PosSubscriptionServices.SmsSetup.name, value);
	};

	const handleSelectAll = () => {
		const sectionValues = getValues(`${sectionName}.subscriptions`);

		const serviceNames = [
			PosSystemName,
			IpPhoneServiceName,
			TapToPayName,
			SmsServiceName,
			SmsMessageName,
			SmsSetupName,
		];

		if (isNailSoftProduct) {
			serviceNames.push(MarketingServiceName);
			serviceNames.push(SalonInTheBoxName);
		}
		if (showMarketingServices && isCalendariseProduct) {
			serviceNames.push(MarketingServiceName);
		}

		serviceNames.forEach(serviceName => {
			setValue(`${serviceName}.isSubscribed`, true as never, {
				shouldDirty: true,
			});
		});
		setDiscount(sectionValues, setValue, sectionName);
	};

	const handleCostChange = serviceName => {
		const sectionValues = getValues(`${sectionName}.subscriptions`);

		const cost = getCost(
			sectionValues[serviceName].billingCadence.name,
			sectionValues[serviceName].quantity,
			PosSubscriptionServices[capitalize(serviceName)].defaultPrice,
		);

		const costFieldName = `${createFieldName(serviceName)}.cost`;

		setValue(costFieldName, floatWithComma2Normalizer(cost), {
			shouldDirty: true,
		});
		trigger(costFieldName);
	};

	const handleStatusChange = (serviceName, value) => {
		const costFieldName = `${createFieldName(serviceName)}.cost`;
		const quantityFieldName = `${createFieldName(serviceName)}.quantity`;

		const errorCost = getFieldState(costFieldName).error;
		const errorQuantity = getFieldState(quantityFieldName).error;

		if (!value && errorCost) {
			clearErrors(costFieldName);
		}
		if (!value && errorQuantity) {
			setValue(quantityFieldName, "1" as never);
			clearErrors(quantityFieldName);
		}
	};

	useEffect(() => {
		if (isCalendariseProduct) {
			setValue(SalonInTheBoxName, null as never);
		}
		if (!showMarketingServices && isCalendariseProduct) {
			setValue(MarketingServiceName, null as never);
			setValue(
				`${IpPhoneServiceName}.billingCadence`,
				initialSubscriptionValues[
					PosSubscriptionServices.IpPhoneService.name
				].billingCadence,
			);
		} else if (
			showMarketingServices &&
			isCalendariseProduct &&
			prevShowMarketingServices !== showMarketingServices
		) {
			setValue(
				MarketingServiceName,
				initialSubscriptionValues[
					PosSubscriptionServices.MarketingServices.name
				],
			);
		}

		setPrevShowMarketingServices(showMarketingServices);
	}, [
		MarketingServiceName,
		SalonInTheBoxName,
		setValue,
		showMarketingServices,
		isCalendariseProduct,
		defaultValues,
		prevShowMarketingServices,
		IpPhoneServiceName,
	]);

	const headerItems = [
		{
			row: [
				{ className: styles.emptyColumn },
				{
					label: "Service / Hardware",
					className: styles.headerColumn,
				},
				{
					label: "Billing Cadence",
					className: styles.headerColumn,
				},
				{
					label: "Quantity",
					className: styles.headerColumn,
				},
				{
					label: "Cost, $",
					className: styles.headerColumn,
				},
				{
					label: "Status",
					className: `${styles.subscriptionTitleColumn} ${styles.headerColumn}`,
				},
				{
					label: (
						<Button
							value="Select&nbsp;All"
							variant="secondary"
							onClick={handleSelectAll}
							className="p-0"
							minWidth="auto"
						/>
					),
					className: `${styles.selectAllColumn} ${styles.headerColumn}`,
				},
				{ className: styles.emptyColumn },
			],
		},
	];

	const posSystemRow = {
		row: useGetRow({
			service: PosSubscriptionServices.PosSystem,
			cadenceDdl: MonthlyAndAnnualDdl,
			handleCadenceChange: handleCadencePosSystemChange,
			handleQuantityChange,
			handleStatusChange: handleStatusPosSystemChange,
			status: sectionValues?.posSystem?.isSubscribed,
			name: PosSystemName,
			isLoading,
			isQuantityDisabled: true,
		}),
	};

	const marketingServicesRow = {
		row: useGetRow({
			service: PosSubscriptionServices.MarketingServices,
			cadenceDdl: MonthlyAndAnnualDdl,
			handleCadenceChange: handleCadenceMarketingServiceChange,
			handleQuantityChange,
			handleStatusChange: handleStatusMarketingServiceChange,
			status: sectionValues?.marketingServices?.isSubscribed,
			name: MarketingServiceName,
			isLoading,
			isQuantityDisabled: true,
		}),
	};

	const ipPhoneServiceRow = {
		row: useGetRow({
			service: PosSubscriptionServices.IpPhoneService,
			cadenceDdl: MonthlyDdl,
			handleQuantityChange,
			handleStatusChange: handleStatusIpPhoneServiceChange,
			status: sectionValues?.ipPhoneService?.isSubscribed,
			name: IpPhoneServiceName,
			isLoading,
		}),
	};

	const tapToPayRow = {
		row: useGetRow({
			service: PosSubscriptionServices.TapToPay,
			cadenceDdl: MonthlyDdl,
			handleQuantityChange,
			handleStatusChange: handleStatusTapToPayChange,
			status: sectionValues?.tapToPay?.isSubscribed,
			name: TapToPayName,
			isLoading,
		}),
	};

	const salonInTheBoxRow = {
		row: useGetRow({
			service: PosSubscriptionServices.SalonInTheBox,
			cadenceDdl: OneTimeDdl,
			handleQuantityChange,
			handleStatusChange: handleStatusSalonInTheBoxChange,
			status: sectionValues?.salonInTheBox?.isSubscribed,
			name: SalonInTheBoxName,
			isLoading,
		}),
	};

	const smsServiceRow = {
		row: useGetRow({
			service: PosSubscriptionServices.SmsService,
			cadenceDdl: CadenceTypesOptions,
			handleCadenceChange,
			handleQuantityChange,
			handleStatusChange: handleStatusSmsServiceChange,
			status: sectionValues?.smsService?.isSubscribed,
			name: SmsServiceName,
			isLoading,
			isQuantityDisabled: true,
		}),
	};

	const smsMessageRow = {
		row: useGetRow({
			service: PosSubscriptionServices.SmsMessage,
			cadenceDdl: CadenceTypesOptions,
			handleCadenceChange,
			handleQuantityChange,
			handleStatusChange: handleStatusSmsMessageServiceChange,
			status: sectionValues?.smsMessage?.isSubscribed,
			name: SmsMessageName,
			isLoading,
		}),
	};

	const smsSetupRow = {
		row: useGetRow({
			service: PosSubscriptionServices.SmsSetup,
			cadenceDdl: CadenceTypesOptions,
			handleCadenceChange,
			handleQuantityChange,
			handleStatusChange: handleStatusSmsSetupServiceChange,
			status: sectionValues?.smsSetup?.isSubscribed,
			name: SmsSetupName,
			isLoading,
		}),
	};

	const bodyItemsNailSoft = [
		posSystemRow,
		marketingServicesRow,
		ipPhoneServiceRow,
		tapToPayRow,
		smsServiceRow,
		smsMessageRow,
		smsSetupRow,
		salonInTheBoxRow,
	];
	const bodyItemsCalendarise = [
		posSystemRow,
		ipPhoneServiceRow,
		tapToPayRow,
		smsServiceRow,
		smsMessageRow,
		smsSetupRow,
	];
	const bodyItemsCalendariseForInsideSales = [
		posSystemRow,
		marketingServicesRow,
		ipPhoneServiceRow,
		tapToPayRow,
		smsServiceRow,
		smsMessageRow,
		smsSetupRow,
	];

	const getBodyItems = () => {
		if (isNailSoftProduct) {
			return bodyItemsNailSoft;
		} else if (isCalendariseProduct && showMarketingServices) {
			return bodyItemsCalendariseForInsideSales;
		} else {
			return bodyItemsCalendarise;
		}
	};

	const bodyItems = getBodyItems();

	return (
		<div className={styles.tableWrapper}>
			<ClassicTable
				headerItems={headerItems}
				bodyItems={bodyItems}
				bordered="horizontal"
				customFooter={
					<Footer
						totalPrice={totalPrice}
						sectionName={sectionName}
						isLoading={isLoading}
						register={register}
						errors={errors}
					/>
				}
			/>
		</div>
	);
};

export default PosSubscription;
